import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {MembershipPlan} from "types/MembershipPlan";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import {formattedDate} from "services/date";
import DeleteMembershipPlan from "pages/MembershipPlan/Delete";
import StatBox from "components/StatBox";
import {IdentificationIcon, UserIcon} from "@heroicons/react/outline";
import StatList from "components/StatList";
import Chip from "components/Chip";

function ViewMembershipPlan() {
    const {id} = useParams()
    const {data: membershipPlan, isLoading} = useQuery(
        ['membership-plan', {id}],
        () => ngage.membershipPlans.get(parseInt(id as string))
    )

    const [deletingMembershipPlan, setDeletingMembershipPlan] = useState<MembershipPlan | null>(null);
    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Prepare action handlers
    function handleDeleteMembershipPlan() {
        if(membershipPlan) setDeletingMembershipPlan(membershipPlan)
    }

    function handleEditMembershipPlan() {
        if(id) navigate(`/membership-plans/${id}/edit`)
    }

    // Prepare page actions
    const actions = <>
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteMembershipPlan} disabled={!membershipPlan || membershipPlan.membership_count > 0}>Delete</Button>
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditMembershipPlan}>Edit</Button>
    </>

    return (
        <Page
            title={membershipPlan?.name || "View Membership Plan"}
            breadcrumbs={{"Audience": "/", "Membership Plans": "/membership-plans", [membershipPlan?.name || "View Membership Plan"]: `/membership-plans/${id}`}}
            actions={actions}
            isLoading={isLoading}
        >
            {!!membershipPlan &&
                <>
                    <section>
                        <StatList>
                            <StatBox name="Memberships" icon={IdentificationIcon} stat={membershipPlan.membership_count} />
                            <StatBox name="Members" icon={UserIcon} stat={membershipPlan.member_count} />
                            {/*<StatBox name="Renewal Rate" icon={PresentationChartLineIcon} stat={"62.6%"} />*/}
                        </StatList>
                    </section>

                    <section className="mt-4">
                        <Card
                            heading="Membership Plan Information"
                            subheading={`A plan used for ${membershipPlan.membership_count?.toLocaleString()} memberships`}
                        >
                            <DescriptionList>
                                <DescriptionListItem span={2}>
                                    <DescriptionTerm>Description</DescriptionTerm>
                                    <DescriptionDetails>
                                        <p className="whitespace-pre-line">{membershipPlan.description ?? `A ${membershipPlan.duration.toHuman()} plan`}</p>
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Duration</DescriptionTerm>
                                    <DescriptionDetails>
                                        {membershipPlan.duration.toHuman()}
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Price</DescriptionTerm>
                                    <DescriptionDetails>
                                        {membershipPlan.price? (membershipPlan.currency.symbol + membershipPlan.price / (Math.pow(10, membershipPlan.currency.precision))) : 'Free'}
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Maximum Members</DescriptionTerm>
                                    <DescriptionDetails>
                                        {membershipPlan.maximum_members} per membership
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Created</DescriptionTerm>
                                    <DescriptionDetails>
                                        {formattedDate(membershipPlan.created_at)}
                                    </DescriptionDetails>
                                </DescriptionListItem>
                            </DescriptionList>
                        </Card>
                    </section>

                    {!!membershipPlan.organisations?.length &&
                        <section className="mt-4">
                            <Card heading="Organisations" subheading="The organisations for which this offer applies">
                                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {membershipPlan.organisations.map((organisation, index) => (
                                        <li key={index}>
                                            <Chip
                                                name={organisation.name}
                                                href={`/organisations/${organisation.id}`}
                                                backgroundSrc={organisation.logo?.preview_url}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        </section>
                    }
                </>
            }

            {/* Delete confirmation modal */}
            <DeleteMembershipPlan
                membershipPlan={deletingMembershipPlan}
                show={membershipPlan === deletingMembershipPlan}
                onCancel={() => setDeletingMembershipPlan(null)}
                onDelete={membershipPlan => {
                    navigate('/membership-plans')
                    notify("success", "Membership Plan Deleted", `${membershipPlan.name} has been permanently removed`)
                }}
                onDeleteFail={membershipPlan => {
                    setDeletingMembershipPlan(null)
                    notify("error", "Membership Plan Not Deleted", `${membershipPlan.name} could not be removed`)
                }}
            />
        </Page>
    );
}

export default ViewMembershipPlan;